import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, TrendingUp, MapPin, Phone, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Hero() {
  const [rating, setRating] = useState(4.2);
  const [reviewCount, setReviewCount] = useState(127);
  useEffect(() => {
    const timer = setTimeout(() => {
      let current = 4.2;
      let count = 127;
      const interval = setInterval(() => {
        current += 0.1;
        count += 13;
        if (current >= 4.9) {
          setRating(4.9);
          setReviewCount(247);
          clearInterval(interval);
        } else {
          setRating(Number(current.toFixed(1)));
          setReviewCount(count);
        }
      }, 200);
      return () => clearInterval(interval);
    }, 800);
    return () => clearTimeout(timer);
  }, []);
  return <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden bg-surface" data-id="element-119">
      {/* Soft Background Blobs */}
      <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none" data-id="element-120">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-blue-soft/60 blur-3xl" data-id="element-121" />
        <div className="absolute top-[50%] -left-[10%] w-[50%] h-[50%] rounded-full bg-gold-soft/60 blur-3xl" data-id="element-122" />
      </div>

      {/* Subtle dot grid */}
      <div className="absolute inset-0 z-0 opacity-[0.15] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle, #0A2540 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-123" />

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-124">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-12 items-center" data-id="element-125">
          {/* Left Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl text-center lg:text-left" data-id="element-126">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-soft text-blue font-semibold text-sm mb-6" data-id="element-127">
              <Star className="w-4 h-4 fill-current" data-id="element-128" />
              <span data-id="element-129">Turning Happy Customers Into 5-Star Reviews</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-navy leading-tight mb-6" data-id="element-130">
              More Reviews.
              <br data-id="element-131" />
              <span className="text-blue" data-id="element-132">More Calls.</span>
              <br data-id="element-133" />
              More Customers.
            </h1>

            <p className="text-lg md:text-xl text-muted mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed" data-id="element-134">
              We help local businesses dominate Google locally. Automate your
              review generation, optimize your profile, and turn your reputation
              into your biggest growth engine.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10 justify-center lg:justify-start" data-id="element-135">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-3.5 text-base font-bold text-white bg-navy hover:bg-navy-deep transition-colors rounded-md shadow-lg shadow-navy/20" data-id="element-136">
                Get Free Google Audit
              </Link>
              <Link to="/pricing" className="inline-flex items-center justify-center px-8 py-3.5 text-base font-bold text-navy bg-transparent border-2 border-gold hover:bg-gold-soft transition-colors rounded-md" data-id="element-137">
                View Packages
              </Link>
            </div>

            {/* Trust Strip */}
            <div className="pt-6 border-t border-gray-200" data-id="element-138">
              <p className="text-sm font-semibold text-navy mb-4 flex items-center justify-center lg:justify-start gap-2" data-id="element-139">
                <span className="flex text-gold" data-id="element-140">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-141" />)}
                </span>
                Trusted by 200+ local businesses
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 items-center justify-center lg:justify-start opacity-60" data-id="element-142">
                <span className="font-heading font-bold text-base md:text-lg text-navy" data-id="element-143">
                  AutoCare
                </span>
                <span className="font-heading font-bold text-base md:text-lg text-navy" data-id="element-144">
                  BrightSmile
                </span>
                <span className="font-heading font-bold text-base md:text-lg text-navy" data-id="element-145">
                  Lima Pizza
                </span>
                <span className="font-heading font-bold text-base md:text-lg text-navy" data-id="element-146">
                  PeakFit
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right Visual */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative w-full max-w-md mx-auto lg:max-w-none lg:mx-0 mt-4 lg:mt-0" data-id="element-147">
            {/* Decorative gold ring */}
            <div className="hidden lg:block absolute -top-8 -right-8 w-32 h-32 border-4 border-gold/30 rounded-full" data-id="element-148" />
            <div className="hidden lg:block absolute -bottom-8 -left-8 w-24 h-24 border-4 border-blue/20 rounded-full" data-id="element-149" />

            {/* Main GBP Card */}
            <motion.div initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.4,
            duration: 0.6
          }} className="relative z-10 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden" data-id="element-150">
              {/* Business cover image */}
              <div className="relative h-32 sm:h-40 bg-gradient-to-br from-blue to-navy overflow-hidden" data-id="element-151">
                <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80" alt="Local business" className="w-full h-full object-cover opacity-80" onError={e => {
                ;
                (e.target as HTMLImageElement).style.display = 'none';
              }} data-id="element-152" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent" data-id="element-153" />
                <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-bold text-navy" data-id="element-154">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-155" />
                  Open Now
                </div>
              </div>

              {/* Business info */}
              <div className="p-5 sm:p-6" data-id="element-156">
                <div className="flex items-start justify-between mb-4" data-id="element-157">
                  <div className="min-w-0 flex-1" data-id="element-158">
                    <h3 className="font-heading font-bold text-navy text-lg sm:text-xl mb-1 truncate" data-id="element-159">
                      Joe's Auto Repair
                    </h3>
                    <p className="text-xs text-muted truncate" data-id="element-160">
                      Auto repair shop · Open · Closes 6PM
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-soft rounded-full flex items-center justify-center ml-3" data-id="element-161">
                    <MapPin className="text-blue w-5 h-5" data-id="element-162" />
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-gray-100" data-id="element-163">
                  <motion.div key={rating} initial={{
                  scale: 1.2,
                  color: '#C8961F'
                }} animate={{
                  scale: 1,
                  color: '#0A2540'
                }} transition={{
                  duration: 0.3
                }} className="text-3xl sm:text-4xl font-heading font-extrabold text-navy" data-id="element-164">
                    {rating.toFixed(1)}
                  </motion.div>
                  <div className="flex-1 min-w-0" data-id="element-165">
                    <div className="flex text-gold mb-1" data-id="element-166">
                      {[1, 2, 3, 4, 5].map(star => <motion.div key={star} animate={{
                      opacity: rating >= star - 0.5 ? 1 : 0.25
                    }} transition={{
                      duration: 0.3
                    }} data-id="element-167">
                          <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-current" data-id="element-168" />
                        </motion.div>)}
                    </div>
                    <div className="text-xs text-muted" data-id="element-169">
                      <motion.span key={reviewCount} initial={{
                      opacity: 0.5
                    }} animate={{
                      opacity: 1
                    }} data-id="element-170">
                        ({reviewCount}) Google Reviews
                      </motion.span>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="grid grid-cols-3 gap-2" data-id="element-171">
                  {[{
                  Icon: Phone,
                  label: 'Call'
                }, {
                  Icon: MapPin,
                  label: 'Directions'
                }, {
                  Icon: Globe,
                  label: 'Website'
                }].map((action, i) => <div key={i} className="flex flex-col items-center gap-1.5 py-2.5 rounded-lg bg-blue-soft/60 text-blue" data-id="element-172">
                      <action.Icon className="w-4 h-4" data-id="element-173" />
                      <span className="text-[10px] sm:text-xs font-semibold" data-id="element-174">
                        {action.label}
                      </span>
                    </div>)}
                </div>
              </div>
            </motion.div>

            {/* Floating Element 1: Calls Up - top right */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.8
          }} className="absolute -right-2 sm:-right-4 lg:-right-6 -top-4 sm:top-4 z-20" data-id="element-175">
              <motion.div animate={{
              y: [-6, 6, -6]
            }} transition={{
              repeat: Infinity,
              duration: 4,
              ease: 'easeInOut'
            }} className="bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-center gap-2 sm:gap-3" data-id="element-176">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 flex-shrink-0" data-id="element-177">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-178" />
                </div>
                <div data-id="element-179">
                  <div className="text-xs sm:text-sm font-bold text-navy whitespace-nowrap" data-id="element-180">
                    +42% Calls
                  </div>
                  <div className="text-[10px] sm:text-xs text-muted" data-id="element-181">
                    This month
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Floating Element 2: New Review - bottom left */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 1
          }} className="absolute -left-2 sm:-left-4 lg:-left-8 -bottom-4 sm:bottom-8 z-20" data-id="element-182">
              <motion.div animate={{
              y: [6, -6, 6]
            }} transition={{
              repeat: Infinity,
              duration: 5,
              ease: 'easeInOut',
              delay: 0.5
            }} className="bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 max-w-[160px] sm:max-w-[200px]" data-id="element-183">
                <div className="flex items-center gap-2 mb-1.5" data-id="element-184">
                  <div className="flex text-gold" data-id="element-185">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-current" data-id="element-186" />)}
                  </div>
                  <span className="text-[10px] sm:text-xs font-bold text-navy" data-id="element-187">
                    New!
                  </span>
                </div>
                <p className="text-[10px] sm:text-xs text-muted leading-snug line-clamp-2" data-id="element-188">
                  "Best service in town. Highly recommend!"
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}